/**
 * Error codes the portal API returns with HTTP 400 when the signed-in user cannot use the portal
 * at all: the learner row is gone, or it is disabled (LearnerPermissionsMiddleware). These are the
 * only 400s that justify discarding the session - /me also answers 400 for `invalid_token_claims`
 * and `toplevel_org_not_found`, which are tenant configuration problems, not user problems.
 */
const invalidUserErrorCodes = ['user_not_found', 'user_disabled'];

export function getDeactivatedAccessMessage(error: unknown) {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const response = 'response' in error ? (error as { response?: Response }).response : undefined;
  const status = response?.status;

  if (status === 400) {
    return 'Your account is currently inactive. Please contact your administrator to request access.';
  }

  return null;
}

/**
 * Whether the error says this specific user cannot use the portal, as opposed to any other reason
 * /me may answer 400. Only this narrower condition may drive a session teardown; the message above
 * stays deliberately broad so no 400 goes unexplained on screen.
 */
export function isDeactivatedAccountError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const response = 'response' in error ? (error as { response?: Response }).response : undefined;
  if (response?.status !== 400) {
    return false;
  }

  const code = getApiErrorCode(error);
  return code != null && invalidUserErrorCodes.includes(code);
}

/**
 * Reads the `code` of the API's error body. ky parses the body onto `error.data` before it throws,
 * so the code is available synchronously here - `error.response.json()` would fail, as populating
 * `data` has already consumed the body stream. The property name is matched case-insensitively:
 * the API serialises with the default web policy (camelCase), but nothing in the response contract
 * enforces that.
 */
function getApiErrorCode(error: object): string | undefined {
  const data = 'data' in error ? (error as { data?: unknown }).data : undefined;
  const body = typeof data === 'string' ? parseJson(data) : data;

  if (!body || typeof body !== 'object') {
    return undefined;
  }

  const code = Object.entries(body).find(([key]) => key.toLowerCase() === 'code')?.[1];
  return typeof code === 'string' ? code.toLowerCase() : undefined;
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    // A body that is not JSON tells us nothing about the user; treat it as an unknown code.
    return undefined;
  }
}
